/**
 * §C.2 · Mi cuenta: lo que es de la persona y no del club.
 *
 * La distinción es la que manda en toda esta sección. El **horario de silencio** es
 * del club —es quien responde de no molestar a ciento veinte casas a las once— y por
 * eso no está aquí. Lo que sí está es lo que solo le importa a uno: su contraseña, su
 * foto, sus avisos, sus sesiones y sus derechos sobre sus datos.
 */

export interface Aviso {
  readonly id: string;
  readonly academy_id: string | null;
  readonly academy_name: string | null;
  readonly type: string;
  readonly title: string;
  readonly body: string | null;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly channel: 'push' | 'email' | 'sms' | 'inapp';
  readonly created_at: string | Date;
  readonly read_at: string | Date | null;
}

export interface PaginaDeAvisos {
  readonly data: readonly Aviso[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
  /** Sin leer **de todos los clubes**, no solo de esta página: es el globito. */
  readonly unread_count: number;
}

export interface Preferencias {
  readonly channels: { push: boolean; email: boolean };
  /** `null` = sin resumen diario. */
  readonly daily_digest_at: string | null;
  /** Minutos antes de la clase. `null` = sin recordatorio. */
  readonly class_reminder_minutes: number | null;
}

export interface SesionAbierta {
  readonly id: string;
  readonly ip: string | null;
  readonly user_agent: string | null;
  readonly created_at: string | Date;
  readonly expires_at: string | Date;
}

export interface ResguardoDeDatos {
  readonly id: string;
  readonly kind: 'export' | 'delete' | 'rectify' | 'restrict';
  readonly status: 'received' | 'in_progress' | 'completed' | 'rejected';
  readonly requested_at: string | Date;
  readonly due_at: string | Date | null;
  readonly completed_at: string | Date | null;
}

/**
 * De qué aparato es una sesión, en palabras.
 *
 * El `user_agent` crudo —«Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)…»— no le
 * dice nada a nadie, y esta lista existe para que alguien pueda reconocer **cuál es
 * la suya** antes de cerrar las demás. Se acierta lo que se puede y lo que no se
 * llama «un navegador», que es honesto y suficiente.
 */
export function aparatoDe(userAgent: string | null): string {
  if (!userAgent) return 'Un navegador';

  const ua = userAgent.toLowerCase();
  const sistema = ua.includes('iphone')
    ? 'iPhone'
    : ua.includes('ipad')
      ? 'iPad'
      : ua.includes('android')
        ? 'Android'
        : ua.includes('macintosh') || ua.includes('mac os')
          ? 'Mac'
          : ua.includes('windows')
            ? 'Windows'
            : ua.includes('linux')
              ? 'Linux'
              : null;

  /* Chrome dice ser Safari, así que se mira primero el que miente. */
  const navegador = ua.includes('edg/')
    ? 'Edge'
    : ua.includes('chrome/')
      ? 'Chrome'
      : ua.includes('firefox/')
        ? 'Firefox'
        : ua.includes('safari/')
          ? 'Safari'
          : null;

  if (sistema && navegador) return `${navegador} en ${sistema}`;
  return sistema ?? navegador ?? 'Un navegador';
}

/** Los estados de una petición de datos, en español. */
export const ESTADOS_DE_PETICION: Readonly<
  Record<ResguardoDeDatos['status'], string>
> = {
  received: 'Recibida',
  in_progress: 'En curso',
  completed: 'Terminada',
  rejected: 'Rechazada',
};
